import { BudgetBounds } from "@/common/utilities/money/budgetBounds";
import { OfficePriceBenchmark } from "./officePriceBenchmark";

/**
 * How a chosen budget compares with what offices of the chosen size usually
 * cost around the location. Thresholds are the benchmark's own percentiles,
 * per seat:
 *
 *   budget   under the 25th percentile: the cheaper end of the market
 *   typical  25th to 75th: the middle half of the market
 *   premium  above the 75th: room for the dearer end
 *
 * Each tier is a fair choice, so none is presented as a warning; the advice
 * says what the budget tends to get instead.
 */
export type TBudgetVerdict = "unset" | "budget" | "typical" | "premium";

export const budgetVerdict = (
    budget: number | null,
    seats: number,
    benchmark: OfficePriceBenchmark
): TBudgetVerdict => {
    if (budget === null) return "unset";
    const perSeat = budget / seats;
    if (perSeat < benchmark.p25) return "budget";
    if (perSeat <= benchmark.p75) return "typical";
    return "premium";
};

/**
 * Whether the budget is under the 10th percentile, where few offices list at
 * all. Worth saying, as a fact rather than a warning, on a budget-tier budget.
 */
export const fewOfficesAtBudget = (
    budget: number | null,
    seats: number,
    benchmark: OfficePriceBenchmark
): boolean => budget !== null && budget / seats < benchmark.p10;

/** The middle half of the market for `seats`, as monthly totals. */
export const typicalTotals = (
    seats: number,
    benchmark: OfficePriceBenchmark
): { low: number; high: number } => ({
    low: Math.round(seats * benchmark.p25),
    high: Math.round(seats * benchmark.p75),
});

const snapDown = (value: number, bounds: BudgetBounds) =>
    bounds.min + Math.floor((value - bounds.min) / bounds.step) * bounds.step;
const snapUp = (value: number, bounds: BudgetBounds) =>
    bounds.min + Math.ceil((value - bounds.min) / bounds.step) * bounds.step;

/**
 * The slider's range narrowed to the part that matters for `seats`: from half
 * the cheap end of the market to twice the dear end.
 *
 * Over the full range (EUR 500 to 20,000) a two-person team's whole market is
 * a sliver at the left end, too thin to aim for. The narrowed range stays
 * inside `bounds` and on its step grid, so every value it offers is one the
 * full range offers too, and the budget still submits as before.
 *
 * The range always stretches to take in `keep`, the budget already chosen, so
 * changing the seat count never quietly cuts or raises a budget the enquirer
 * picked; the advice just re-judges it.
 *
 * Falls back to `bounds` when the narrowed range would hold fewer than ten
 * steps, which only happens when the market sits outside the range anyway.
 */
export const zoomBudgetBounds = (
    bounds: BudgetBounds,
    seats: number,
    benchmark: OfficePriceBenchmark,
    keep: number | null = null
): BudgetBounds => {
    let min = Math.max(
        bounds.min,
        snapDown(seats * benchmark.p10 * 0.5, bounds)
    );
    let max = Math.min(bounds.max, snapUp(seats * benchmark.p90 * 2, bounds));
    if (max - min < bounds.step * 10) return bounds;
    if (keep !== null) {
        min = Math.max(bounds.min, Math.min(min, snapDown(keep, bounds)));
        max = Math.min(bounds.max, Math.max(max, snapUp(keep, bounds)));
    }
    return { min, max, step: bounds.step };
};

/** One coloured stretch of the slider's track, in percent of its width. */
export interface BudgetZone {
    verdict: Exclude<TBudgetVerdict, "unset">;
    widthPercent: number;
}

/**
 * The track split into the three tiers for `seats`, left to right. A zone
 * that falls outside the slider's range gets no width.
 */
export const budgetZones = (
    bounds: BudgetBounds,
    seats: number,
    benchmark: OfficePriceBenchmark
): BudgetZone[] => {
    const at = (perSeat: number) => {
        const fraction =
            (seats * perSeat - bounds.min) / (bounds.max - bounds.min);
        return Math.min(100, Math.max(0, fraction * 100));
    };
    const edges = [0, at(benchmark.p25), at(benchmark.p75), 100];
    const verdicts = ["budget", "typical", "premium"] as const;
    return verdicts.map((verdict, i) => ({
        verdict,
        widthPercent: edges[i + 1] - edges[i],
    }));
};
